<template>
	<ParamWrapper v-bind="wrapperProps" @input-data-closed="onDataInputMenuClose">
		<component :is="component" v-bind="componentProps" v-on="componentListeners" :showClearButton="hasClearButton" />
		<template v-if="!customLayoutMode" v-slot:input-data>
			<slot name="input-data">
				<SelectDataInputMenu
					ref="data-input-menu"
					v-on="dataInputListeners"
					:param="param"
					:items="selectItems"
					@update-external-localValue="updateSelectValue"
				/>
			</slot>
		</template>
	</ParamWrapper>
</template>

<script>
import ParamTypeExtenderDataInputMenuMixin from './mixins/ParamTypeExtenderDataInputMenuMixin';
import ParamTypeMixin from './mixins/ParamTypeMixin';
import SelectItemsMixin from './mixins/SelectItemsMixin';
import ThisFlowMixin from './mixins/ThisFlowMixin';
import ParamTypeHelper from './param-type-helper';
import { SELECT_TAB_KEY } from '../../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import SelectDataInputMenu from '../DataInputMenus/SelectDataInputMenu';
import { SELECT_PARAM_TYPE } from '../block-editor-params.const';
import DefaultPropertyDropdownInput from '../inputsComponents/DefaultPropertyDropdownInput';
import DefaultPropertyInput from '../inputsComponents/DefaultPropertyInput';
import SelectCustomTabsInput from '../inputsComponents/SelectCustomTabsInput.vue';

export default {
	name: 'SelectParamType',
	mixins: [ParamTypeMixin, ParamTypeExtenderDataInputMenuMixin, ThisFlowMixin, SelectItemsMixin],
	components: { SelectDataInputMenu, DefaultPropertyDropdownInput, DefaultPropertyInput, SelectCustomTabsInput },
	computed: {
		customLayoutMode() {
			if (this.param.layout in this.customLayoutModeComponents) {
				return this.param.layout;
			}
			return null;
		},
		fromSelectOptions() {
			return this.curTabCode === SELECT_TAB_KEY;
		},
		componentConfig() {
			const EXTERNAL_VALUE_PROPS = {
				...this.$attrs,
				value: this.localValueDisplay,
				rules: this.fieldRules,
				inputFieldHint: this.param.placeholder,
				isDropdownOpen: this.showDataMenu,
				isFocused: this.isFocused,
				showDataMenu: this.showDataMenu,
				readonly: this.readonly,
				ref: 'richEditor',
			};
			const RICH_TEXT_BOX_PROPS = {
				...this.$attrs,
				inputFieldHint: this.param.placeholder,
				value: this.localValue,
				ref: 'richEditor',
				isFocused: this.isFocused,
				showDataMenu: this.showDataMenu,
				readonly: this.readonly,
			};
			const EXTERNAL_VALUE_LISTENERS = {
				'clear-value': this.clearValue,
				focus: this.onInputFocus,
				blur: this.onInputBlur,
				click: this.onInputClick,
				open: this.openDataInputMenu,
			};
			const RICH_TEXT_BOX_LISTENERS = {
				focus: this.onInputFocus,
				blur: this.onInputBlur,
				click: this.onInputClick,
				input: (val) => (this.localValue = val),
				open: this.openDataInputMenu,
			};
			const REGULAR_PROPERTY_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				DefaultPropertyInput,
				RICH_TEXT_BOX_PROPS,
				RICH_TEXT_BOX_LISTENERS
			);
			const REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				DefaultPropertyDropdownInput,
				EXTERNAL_VALUE_PROPS,
				EXTERNAL_VALUE_LISTENERS
			);
			if (this.customLayoutMode) {
				// If more custom layouts are added, these props and listeners should be moved to a
				// computed value that will return value based on this.customLayoutModeComponents[this.customLayoutMode]
				const CUSTOM_TAB_PROPS = { selectItems: this.selectItems, value: this.localValue };
				const CUSTOM_TAB_LISTENERS = { 'change-tab': this.changeTab };
				const CUSTOM_TABS_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
					this.customLayoutModeComponents[this.customLayoutMode],
					CUSTOM_TAB_PROPS,
					CUSTOM_TAB_LISTENERS
				);
				return CUSTOM_TABS_INPUT_CONFIG;
			}
			return this.fromSelectOptions ? REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG : REGULAR_PROPERTY_INPUT_CONFIG;
		},
		component() {
			return this.componentConfig.component;
		},
		componentProps() {
			return this.componentConfig.props;
		},
		componentListeners() {
			return this.componentConfig.listeners;
		},
		hasClearButton() {
			return [SELECT_PARAM_TYPE].includes(this.param.type);
		},
	},
	methods: {
		changeTab(tab) {
			this.localValue = tab;
		},
		addSelectTabItem() {
			const fieldName = this.param.name ?? 'fieldName';
			this.addTabItem(SELECT_TAB_KEY, fieldName, true);
		},
		clearValue() {
			this.updateSelectValue({ id: '', name: '', value: '' }); // this doesn't necessarily clears the value..
		},
		updateSelectValue(payload) {
			this.updateConstantValueFromDataInputMenu(payload);
		},
	},
	data() {
		return {
			customLayoutModeComponents: { tabs: SelectCustomTabsInput },
		};
	},
	mounted() {
		this.addSelectTabItem();
		// applies label of selected option, if exists
		this.applyLabelFromOptions(this.selectItems);
	},
	watch: {
		componentConfig: {
			handler() {
				this.$nextTick(() => {
					this.setInputElement(this.$refs['richEditor']?.$el);
				});
			},
		},
	},
};
</script>
<style lang="scss" scoped></style>
